import { Box, Typography as ChronoUiTypography, IconButton, styled, Tooltip } from '@chronosphereio/chrono-ui';
import { memo, useMemo } from 'react';
import {
  AppMetadataProps,
  formatValue,
  SeriesConfig,
  SeriesMenuItemOptions,
  TooltipContextMenuCtx,
  UnitOptions,
} from '@/core';
import { SeriesMarker } from '../PanelTooltip';
import { useSeriesActionContext } from './SeriesActionProvider';
import { SeriesTooltipActions } from './SeriesTooltipActions';
import {
  EMPHASIZED_SERIES_DESCRIPTION,
  NEARBY_SERIES_DESCRIPTION,
  SeriesInfoContext,
  TOOLTIP_LABELS_MAX_WIDTH,
  TooltipConfig,
  TooltipLink,
} from './tooltip-model';
import { checkUnitPrecision } from './utils';

export type SeriesInfoProps = {
  links?: TooltipLink[];
  isTooltipPinned?: boolean;
  config?: SeriesConfig;
  unit?: UnitOptions;
  enableTooltipPreciseValues?: boolean;
  markerType?: 'solid' | 'striped';
  tooltipContextMenuProps?: TooltipConfig['tooltipContextMenuProps'];
  tooltipContextMenuData?: TooltipConfig['tooltipContextMenuData'];
  appMetadata?: AppMetadataProps;
  clearPinned?: () => void;
} & SeriesInfoContext;

export const SeriesInfo = memo(function SeriesInfo(props: SeriesInfoProps) {
  const {
    seriesId,
    seriesName,
    formattedY,
    markerColor,
    markerType = 'solid',
    emphasizeText = false,
    wrapSeriesName,
    isTooltipPinned,
    config,
    x: timestamp,
    y,
    unit,
    enableTooltipPreciseValues,
    tooltipContextMenuProps,
    tooltipContextMenuData,
    appMetadata,
  } = props;

  const renderSeriesAction = useSeriesActionContext()?.renderSeriesAction;
  const getSeriesById = useSeriesActionContext()?.getSeriesById;

  const seriesMenuOptions = useMemo(() => {
    const seriesMenuOptions: SeriesMenuItemOptions[] = [];
    const ctx: TooltipContextMenuCtx = {
      timestamp,
      appMetadata,
      isTooltipPinned: !!isTooltipPinned,
      seriesKey: seriesId,
    };
    const seriesWithMapping = getSeriesById?.(seriesId);
    if (seriesWithMapping) {
      // Remove implementation details from series
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { queryResultsIndex, mappingIndex, ...series } = seriesWithMapping;
      if (tooltipContextMenuProps?.seriesMenuItems) {
        const seriesMenuItems = tooltipContextMenuProps?.seriesMenuItems(ctx, series);
        seriesMenuItems.forEach((item) => {
          seriesMenuOptions.push(item);
        });
      }

      if (tooltipContextMenuData) {
        const tooltipContextMenu = tooltipContextMenuData[queryResultsIndex];
        if (tooltipContextMenu) {
          const seriesMenuItems = tooltipContextMenu(ctx, series);
          seriesMenuItems.forEach((item) => {
            seriesMenuOptions.push(item);
          });
        }
      }
    }

    return seriesMenuOptions;
  }, [
    timestamp,
    isTooltipPinned,
    getSeriesById,
    seriesId,
    appMetadata,
    tooltipContextMenuData,
    tooltipContextMenuProps,
  ]);

  // Add space after commas so whiteSpace style to wrap series name to multiple lines works
  const formattedSeriesInfo = wrapSeriesName ? seriesName.replace(/[,]/g, ', ') : seriesName;
  const hasLinks = props.links && props.links.length > 0;

  // Get formatted y value.
  let yValueStr = String(y);
  if (formattedY !== undefined) {
    // Needed for legacy LineChartBase and forked TimeSeriesChart in monitors where formatter
    // is called in nearby series lookup (legacyCheckforNearbySeries).
    yValueStr = formattedY;
  } else if (config?.unit !== undefined) {
    const adjustedUnit = checkUnitPrecision(config.unit, enableTooltipPreciseValues);
    yValueStr = formatValue(y, adjustedUnit); // Unit from series config is used for right yAxis.
  } else if (unit !== undefined) {
    // Default unit for left yAxis, calling checkUnitPrecision is not needed since it is already called in TimeChartTooltip.
    yValueStr = formatValue(y, unit);
  }

  return (
    <Box
      sx={{
        display: 'flex',
        maxWidth: '100%',
        alignItems: 'center',
      }}
    >
      <Box
        paddingY={0}
        sx={{
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <SeriesMarker color={config?.color ?? markerColor} striped={markerType === 'striped'} />
          <Box
            component="span"
            sx={{
              display: 'block',
              flex: '1',
              minWidth: 150,
              maxWidth: TOOLTIP_LABELS_MAX_WIDTH,
            }}
            aria-label={emphasizeText ? EMPHASIZED_SERIES_DESCRIPTION : NEARBY_SERIES_DESCRIPTION}
          >
            <SeriesInfoTypography
              variant="body2"
              title={seriesName} // Shows full series name on hover
              emphasizeText={emphasizeText}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: wrapSeriesName ? 'normal' : 'nowrap',
              }}
            >
              {formattedSeriesInfo}
            </SeriesInfoTypography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          paddingLeft: 1.5,
          textAlign: 'right',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap', // Ensure long values with units do not wrap in EmphasizedSeriesSection
        }}
        aria-label="tooltip series value"
      >
        <SeriesInfoTypography variant="body2" emphasizeText={emphasizeText}>
          {yValueStr}
        </SeriesInfoTypography>
      </Box>

      {isTooltipPinned && !hasLinks && seriesMenuOptions.length === 1 && (
        <SeriesActionIconButton seriesAction={seriesMenuOptions[0]} />
      )}

      {renderSeriesAction && (
        <Box
          sx={{
            textAlign: 'right',
            paddingLeft: 1,
            verticalAlign: 'middle',
          }}
        >
          {renderSeriesAction(props?.seriesId, {
            isTooltipPinned: isTooltipPinned,
          })}
        </Box>
      )}

      {isTooltipPinned && (hasLinks || seriesMenuOptions.length > 1) && (
        <Box
          sx={{
            textAlign: 'right',
            paddingLeft: 1,
          }}
        >
          <SeriesTooltipActions
            seriesInfo={props}
            links={props.links}
            seriesActions={seriesMenuOptions}
            onClick={props.clearPinned}
          />
        </Box>
      )}
    </Box>
  );
});

const SeriesInfoTypography = styled(ChronoUiTypography, {
  shouldForwardProp: (prop) => prop !== 'emphasizeText',
})<{ emphasizeText?: boolean }>(({ theme, emphasizeText }) => ({
  // Use bold font weight so emphasized series in tooltip is obvious in dark mode.
  fontWeight: emphasizeText ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  lineHeight: '24px',
}));

interface SeriesActionIconButtonProps {
  seriesAction?: SeriesMenuItemOptions;
}

const SeriesActionIconButton = ({ seriesAction }: SeriesActionIconButtonProps) => {
  if (!seriesAction) return null;

  return (
    <Box
      sx={{
        textAlign: 'right',
        paddingLeft: 1,
        lineHeight: '18px', // this is needed to prevent the table row height from growing
      }}
    >
      <Tooltip Content={seriesAction.title}>
        <a href={seriesAction.href} target="_blank" rel="noopener noreferrer">
          <IconButton
            size="small"
            onClick={seriesAction.onClick}
            sx={{ padding: '1px' }}
            aria-label={seriesAction.description}
          >
            <seriesAction.icon />
          </IconButton>
        </a>
      </Tooltip>
    </Box>
  );
};
